/**
 * TrafficStars Click ID Management Utilities
 * Handles capturing, storing, and retrieving TrafficStars click IDs
 */

const CLICK_ID_COOKIE_NAME = 'ts_click_id';
const CLICK_ID_STORAGE_KEY = 'ts_click_id';
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Extract TrafficStars click ID from URL query parameters
 * Supports both 'ts_click_id' and 'clickid' param names
 */
export function extractClickIdFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('ts_click_id') || urlObj.searchParams.get('clickid');
  } catch {
    return null;
  }
}

/**
 * Store click ID in cookie (client-side)
 * Sets a secure, same-site cookie with 30-day expiration
 */
export function setClickIdCookie(clickId: string): void {
  if (typeof document === 'undefined') return;
  
  const expires = new Date();
  expires.setSeconds(expires.getSeconds() + COOKIE_MAX_AGE);
  
  document.cookie = `${CLICK_ID_COOKIE_NAME}=${clickId}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}

/**
 * Get click ID from cookie (client-side)
 */
export function getClickIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  
  const match = document.cookie.match(new RegExp(`(^| )${CLICK_ID_COOKIE_NAME}=([^;]+)`));
  return match ? match[2] : null;
}

/**
 * Store click ID in localStorage (client-side fallback)
 */
export function setClickIdStorage(clickId: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    window.localStorage.setItem(CLICK_ID_STORAGE_KEY, clickId);
  } catch (error) {
    console.error('Failed to store click ID in localStorage:', error);
  }
}

/**
 * Get click ID from localStorage
 */
export function getClickIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    return window.localStorage.getItem(CLICK_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Get click ID from either cookie or localStorage
 * Tries cookie first, falls back to localStorage
 */
export function getStoredClickId(): string | null {
  return getClickIdFromCookie() || getClickIdFromStorage();
}

/**
 * Parse click ID from cookie header (server-side)
 */
export function getClickIdFromCookieHeader(cookieHeader: string | null): string | null {
  if (!cookieHeader) return null;
  
  const match = cookieHeader.match(new RegExp(`${CLICK_ID_COOKIE_NAME}=([^;]+)`));
  return match ? match[1] : null;
}

